import type { ProductIosPrice } from '@/types';

/** What the iOS price inputs hold: either number can be blank while typing. */
export interface IosPriceFormValue {
  inr?: number | null;
  usd?: number | null;
}

const isBlank = (v: number | null | undefined): boolean => v === undefined || v === null;

/**
 * Turn the two form inputs into the API value. Both blank = null ("same as
 * Android", which also clears a price that was saved before). Both filled = the
 * pair. One filled is not valid: the backend wants both, so the form rejects it
 * before this runs and this returns undefined for it.
 */
export function iosPriceForSave(
  value: IosPriceFormValue | null | undefined
): ProductIosPrice | null | undefined {
  const inrBlank = isBlank(value?.inr);
  const usdBlank = isBlank(value?.usd);
  if (inrBlank && usdBlank) return null;
  if (inrBlank || usdBlank) return undefined;
  return { inr: Number(value?.inr), usd: Number(value?.usd) };
}

/** Form rule for one of the two inputs: filling only one of them is an error. */
export function iosPricePairRule(other: 'inr' | 'usd'): (form: {
  getFieldValue: (path: string[]) => unknown;
}) => {
  validator: (_: unknown, value: unknown) => Promise<void>;
} {
  return ({ getFieldValue }) => ({
    validator(_, value) {
      const otherValue = getFieldValue(['iosPrice', other]);
      const thisBlank = value === undefined || value === null;
      const otherBlank = otherValue === undefined || otherValue === null;
      if (thisBlank !== otherBlank) {
        return Promise.reject(new Error('Fill both iOS prices, or clear both'));
      }
      return Promise.resolve();
    },
  });
}

/** Text for the list and show pages. */
export function describeIosPrice(value: ProductIosPrice | null | undefined): string {
  if (!value) return 'Same as Android';
  return `$${value.usd} / ₹${value.inr}`;
}
